import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';

import { ThresholdStreakTaskCurrentThresholdState } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Progress as ProgressBar } from '@sorare/core/src/atoms/loader/Progress';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Progress } from '@sorare/core/src/components/rivals/branded/Progress';
import { isType } from '@sorare/core/src/lib/gql';

import type { StreakProgress_managerTaskInterface } from './__generated__/index.graphql';

type Props = {
  managerTask: StreakProgress_managerTaskInterface;
  showLabel?: boolean;
};
export const StreakProgress = ({ managerTask, showLabel = true }: Props) => {
  const isThresholdsStreakTask = isType(managerTask, 'ThresholdsStreakTask');
  if (!isThresholdsStreakTask) {
    return null;
  }
  const highlightNext = [
    ThresholdStreakTaskCurrentThresholdState.UPCOMING_LINEUP_SET,
    ThresholdStreakTaskCurrentThresholdState.UPCOMING_LINEUP_NOT_SET,
  ].includesValue(managerTask.currentThresholdStatus.state);
  const showProgress = [
    ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT,
    ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_IN_PROGRESS,
    ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_MISSED,
    ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_NOT_SET,
    ThresholdStreakTaskCurrentThresholdState.COMPLETED,
  ].includesValue(managerTask.currentThresholdStatus.state);
  const highlightNextcolor =
    managerTask.currentThresholdStatus.state ===
    ThresholdStreakTaskCurrentThresholdState.UPCOMING_LINEUP_SET
      ? 'var(--c-white)'
      : 'var(--c-nd-500)';
  const progressIncrement = [
    ThresholdStreakTaskCurrentThresholdState.COMPLETED,
    ThresholdStreakTaskCurrentThresholdState.CLAIMED,
  ].includesValue(managerTask.currentThresholdStatus.state)
    ? 1
    : 0;

  return showProgress ? (
    <Horizontal>
      <ProgressBar
        value={managerTask.currentThresholdStatus.liveScore || 0}
        max={managerTask.currentThreshold.score}
        color={
          (managerTask.currentThresholdStatus.liveScore || 0) >=
          managerTask.currentThreshold.score
            ? 'var(--c-yellow-400)'
            : 'var(--c-white)'
        }
        bgColor="var(--c-nd-200)"
      />
      <LabelS bold>
        <FormattedNumber
          value={managerTask.currentThresholdStatus.liveScore || 0}
        />
      </LabelS>
    </Horizontal>
  ) : (
    <Progress
      progress={managerTask.progress + progressIncrement || 0}
      target={managerTask.target}
      color="var(--c-nd-200)"
      activeColor="var(--c-progress-active-color, var(--c-rewards-orange))"
      highlightNextColor={highlightNext ? highlightNextcolor : undefined}
      showLabel={showLabel}
    />
  );
};
StreakProgress.fragments = {
  managerTask: gql`
    fragment StreakProgress_managerTaskInterface on ManagerTaskInterface {
      id
      progress
      ... on ThresholdsStreakTask {
        id
        target
        currentThresholdStatus {
          liveScore
          state
        }
        currentThreshold {
          score
        }
      }
    }
  ` as TypedDocumentNode<StreakProgress_managerTaskInterface>,
};
